import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import { TrialHighlight } from "../../components/TrialHighlight";
import {
  TIER_BANDS,
  STARTING_BAND,
  TIER_SOURCE,
  bandRange,
  formatInr,
  formatInrPaise,
  type TierBand,
} from "../../domain/tiers";
import { PROVIDER_LOWER } from "../../domain/labels";

/**
 * What this costs — shown at registration.
 *
 * 🔑 IT IS A DISCLOSURE, NOT A PICKER, and that is a design constraint rather
 * than an unfinished feature. `io.ankhos/templates/ankhos-tiers-v1.md` says the
 * band is "derived from a measurement, never self-declared", and it is
 * re-derived at every renewal from the active subscriber count. A dropdown here
 * would let an applicant assert a band, which is the one thing the money path
 * refuses — and it would look completely ordinary on screen.
 *
 * 🔑 SO THERE IS NOTHING TO CHOOSE, AND SAYING SO IS THE POINT. Everybody
 * registering starts in Launch, because everybody registering has no
 * subscribers yet. The table is here so nobody is surprised by the second
 * month's invoice, not so anybody can act on it.
 *
 * 🪤 THE WORD "TIER" IS ALREADY TAKEN ON THIS SCREEN. The form's "Provider
 * tier" field is ISP | VNO | OPERATOR, a licence classification. This component
 * says "plan" and "band" and never the bare word, because two different
 * meanings one field apart is how a provider reads the wrong number.
 */
export function TierDisclosure({ dense = false }: { dense?: boolean }) {
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
        What AnkhOS costs
      </Typography>
      <Box sx={{ mb: 2 }}>
        <TrialHighlight />
      </Box>

      <Alert severity="success" sx={{ mb: 2 }}>
        <AlertTitle>
          You start on {STARTING_BAND.label}
          {STARTING_BAND.price !== null ? ` — ${formatInr(STARTING_BAND.price)} a month` : ""}
        </AlertTitle>
        There is nothing to choose here. Your plan follows your{" "}
        <strong>active subscriber count</strong>, so a new {PROVIDER_LOWER} starts on{" "}
        {STARTING_BAND.label} and moves up only when the subscribers are actually there.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell>Active subscribers</TableCell>
              <TableCell align="right">Per month</TableCell>
              <TableCell align="right">Beyond the allowance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TIER_BANDS.map((b: TierBand) => {
              const starting = b.tier === STARTING_BAND.tier;
              return (
                <TableRow key={b.tier} selected={starting}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: starting ? 700 : 400 }}>
                      {b.label}
                    </Typography>
                    {starting && (
                      <Chip size="small" color="success" label="You start here" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>{bandRange(b)}</TableCell>
                  <TableCell align="right">
                    {/* A number here would quote a price for a deal nobody has
                        negotiated, which is worse than showing nothing. */}
                    {b.price === null ? "Let's talk" : formatInr(b.price)}
                  </TableCell>
                  <TableCell align="right">
                    {/* The logins column is GONE, not blanked: logins are
                        unlimited at every plan now, so a column of identical
                        "Unlimited" cells would read as a comparison where there
                        is nothing to compare. What varies is the overage rate. */}
                    {b.overagePerSubscriber === null
                      ? "—"
                      : `${formatInrPaise(b.overagePerSubscriber)} / extra`}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle>How your plan is worked out</AlertTitle>
        We count the end customers currently subscribed to your service —{" "}
        <strong>including free and trial customers</strong>, because they use the platform too.
        Your own staff, admin, support and API accounts are not counted. The count is re-checked
        each billing period: if it falls, your plan falls with it from the next period. If you go
        over, we tell you and give you time — we never cut you off.
      </Alert>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        {/* ⚠️ Deliberately says "indicative". These numbers are a MIRROR of the
            catalog; the figure that reaches an invoice is resolved from the
            compiled rows, and the two can only be compared once the gateway can
            reach radbilling. Claiming more than that would be a claim this
            screen cannot support. */}
        Indicative prices, approved {TIER_SOURCE.approvedOn}, effective {TIER_SOURCE.effectiveFrom}.
        The amount on your invoice is the one recorded against your account at the time it is
        raised. Source: <code>{TIER_SOURCE.repo}/{TIER_SOURCE.file}</code> v{TIER_SOURCE.templateVersion}.
      </Typography>
    </Box>
  );
}
